/** RBAC permission definitions */
export const ROLES = ['admin', 'editor', 'viewer', 'agent'] as const
export type Role = (typeof ROLES)[number]

export const PERMISSIONS: Record<Role, string[]> = {
  admin: ['tenant:manage', 'user:manage', 'doc:*', 'storage:*', 'site:*', 'folder:*', 'key:*', 'audit:read'],
  editor: [
    'doc:create', 'doc:read', 'doc:update', 'doc:delete', 'doc:search', 'doc:share',
    'storage:read', 'storage:upload', 'storage:delete',
    'site:read', 'site:upload', 'site:delete', 'site:rollback',
    'folder:read', 'folder:create', 'folder:update', 'folder:delete',
  ],
  viewer: ['doc:read', 'doc:search', 'storage:read', 'site:read', 'folder:read'],
  agent: ['doc:read', 'doc:search'],
}

/** All valid API key scopes */
export const VALID_SCOPES = [
  'doc:read', 'doc:create', 'doc:update', 'doc:delete', 'doc:search', 'doc:share',
  'storage:read', 'storage:upload', 'storage:delete',
  'site:read', 'site:upload', 'site:delete', 'site:rollback',
  'folder:read', 'folder:create', 'folder:update', 'folder:delete',
  'key:read', 'key:create', 'key:update', 'key:revoke',
] as const

/** Wildcard scopes — grants all actions for a resource */
export const WILDCARD_SCOPES = ['doc:*', 'storage:*', 'site:*', 'folder:*', 'key:*', '*:*'] as const

/** All accepted scopes (valid + wildcards) */
export const ALL_SCOPES = [...VALID_SCOPES, ...WILDCARD_SCOPES] as const

/** Scope categories for UI grouping */
export const SCOPE_CATEGORIES = {
  doc: { label: 'Documents', scopes: ['doc:read', 'doc:create', 'doc:update', 'doc:delete', 'doc:search', 'doc:share'] },
  storage: { label: 'Storage', scopes: ['storage:read', 'storage:upload', 'storage:delete'] },
  site: { label: 'Static Sites', scopes: ['site:read', 'site:upload', 'site:delete', 'site:rollback'] },
  folder: { label: 'Folders', scopes: ['folder:read', 'folder:create', 'folder:update', 'folder:delete'] },
  key: { label: 'API Keys', scopes: ['key:read', 'key:create', 'key:update', 'key:revoke'] },
} as const

/** Rate limit configs (GH-237: ONBOARDING_PACKAGES_* entries appended) */
export const RATE_LIMITS = {
  api: { limit: 100, windowSec: 60 },
  search: { limit: 50, windowSec: 60 },
  searchExpand: { limit: 10, windowSec: 60 }, // stricter limit when expand=true (AI cost surface)
  suggest: { limit: 100, windowSec: 60 },
  web: { limit: 1000, windowSec: 60 },
  bulkAction: { limit: 10, windowSec: 60 }, // P4 batch review: 10 bulk ops per user per minute
  /** GH-237: list/read onboarding packages */
  ONBOARDING_PACKAGES_READ: { limit: 120, windowSec: 60 },
  /** GH-237: create/update/delete onboarding packages */
  ONBOARDING_PACKAGES_WRITE: { limit: 30, windowSec: 60 },
  /** GH-237: lifecycle transition mutations */
  ONBOARDING_PACKAGES_TRANSITION: { limit: 20, windowSec: 60 },
  /** GH-235: read completeness for an onboarding package (on-demand compute) */
  ONBOARDING_PACKAGE_COMPLETENESS_READ: { limit: 60, windowSec: 60 },
  /** GH-235: update source_group tag on a batch↔package link */
  ONBOARDING_PACKAGE_TAGGING_UPDATE: { limit: 60, windowSec: 60 },
  /** GH-236: create an approval row for a package (POST /approvals) */
  PACKAGE_APPROVALS_WRITE: { limit: 20, windowSec: 60 },
  /** GH-236: list approval rows for a package (GET /approvals) */
  PACKAGE_APPROVALS_READ: { limit: 60, windowSec: 60 },
  /** GH-236: assign or clear the reviewer on a package (PATCH /reviewer) */
  PACKAGE_REVIEWER_WRITE: { limit: 30, windowSec: 60 },
  /** GH-236: read trust summary for a package (GET /trust) */
  PACKAGE_TRUST_READ: { limit: 60, windowSec: 60 },
  /** GH-236: read ambiguity queue for a package (GET /ambiguities) */
  PACKAGE_AMBIGUITIES_READ: { limit: 60, windowSec: 60 },
  /** GH-268: seed/cleanup demo sample-pack (POST + DELETE /onboarding-packages/sample-pack). */
  SAMPLE_PACK: { limit: 10, windowSec: 60 * 60 },
} as const

/** Access levels for documents */
export const ACCESS_LEVELS = ['private', 'specific', 'public'] as const
export type AccessLevel = (typeof ACCESS_LEVELS)[number]

/** Auth token TTLs */
export const TOKEN_TTL = {
  accessToken: 60 * 60 * 1000, // 1 hour
  refreshToken: 30 * 24 * 60 * 60 * 1000, // 30 days
  shareLink: 90 * 24 * 60 * 60 * 1000, // 90 days
} as const

/** API key prefix */
export const API_KEY_PREFIX = 'aw_'

/** Supported AI providers with available models */
export const AI_PROVIDERS = {
  openai: { name: 'OpenAI', models: ['gpt-4o', 'gpt-4o-mini', 'o3-mini', 'gpt-4-turbo'] },
  anthropic: { name: 'Anthropic', models: ['claude-sonnet-4-6', 'claude-haiku-4-5-20251001', 'claude-opus-4-6'] },
  google: { name: 'Google Gemini', models: ['gemini-2.5-flash', 'gemini-2.5-flash-lite', 'gemini-2.5-pro', 'gemini-2.0-flash', 'gemini-3-flash-preview', 'gemini-3.1-pro-preview', 'gemini-3.1-flash-lite-preview'] },
  openrouter: { name: 'OpenRouter', models: ['auto'] },
  minimax: { name: 'MiniMax', models: ['MiniMax-M1', 'MiniMax-T1'] },
  alibaba: { name: 'Alibaba', models: ['qwen-turbo', 'qwen-plus', 'qwen-max'] },
} as const

/** AI rate limit: requests per user per minute */
export const AI_RATE_LIMIT = { maxRequests: 15, intervalMs: 60_000 } as const

// ── Onboarding Package — GH-237 ───────────────────────────────────────────────

/** All valid lifecycle states for an onboarding package (ordered: happy path first). */
export const ONBOARDING_PACKAGE_LIFECYCLE_VALUES = [
  'draft',
  'collecting',
  'reviewing',
  'approved',
  'released',
  'blocked',
] as const

/** All valid criticality levels for an onboarding package (ordered: low → critical). */
export const CRITICALITY_VALUES = ['low', 'medium', 'high', 'critical'] as const

// ── Source Group — GH-235 ────────────────────────────────────────────────────

/**
 * Canonical source-group taxonomy (fixed 7 values — locked GH-235 plan.md §1).
 * `other` is the catch-all; null `source_group` on the DB join row is treated as `other`
 * at the service layer.
 */
export const SOURCE_GROUPS = [
  'contracts',
  'policies',
  'invoices',
  'transcripts',
  'decks',
  'compliance_evidence',
  'other',
] as const

/**
 * All valid per-group states (locked GH-235 plan.md §2).
 * Used as the zod enum tuple for sourceGroupStateSchema.
 */
export const SOURCE_GROUP_STATE_VALUES = [
  'missing',
  'partial',
  'complete',
  'failed',
] as const

// ── Trust & Approval — GH-236 ────────────────────────────────────────────────

/**
 * All valid composite trust statuses for an onboarding package (locked GH-236 plan.md §6).
 * Derived at query time; never persisted.
 *   safe_to_use  — ≥1 approval, no blockers.
 *   needs_review — ambiguities or low-confidence items; no hard blockers.
 *   blocked      — hard blockers present (missing source groups, failed items, etc.).
 */
export const TRUST_STATUSES = ['safe_to_use', 'needs_review', 'blocked'] as const

/**
 * Allowed lifecycle transitions for onboarding packages.
 *
 * Encodes the full state machine — used by `validateLifecycleTransition` in the
 * shared schema helper and enforced again in the service layer.
 *
 * Transition rules (locked — GH-237 plan.md):
 *   draft       → collecting | blocked
 *   collecting  → reviewing  | blocked | draft
 *   reviewing   → approved   | collecting | blocked
 *   approved    → released   | reviewing  | blocked
 *   released    → blocked    (informational only in P1 — no downstream side-effects)
 *   blocked     → draft | collecting | reviewing | approved  (resume to prior level)
 */
export const LIFECYCLE_TRANSITIONS: Record<
  (typeof ONBOARDING_PACKAGE_LIFECYCLE_VALUES)[number],
  ReadonlyArray<(typeof ONBOARDING_PACKAGE_LIFECYCLE_VALUES)[number]>
> = {
  draft:      ['collecting', 'blocked'],
  collecting: ['reviewing', 'blocked', 'draft'],
  reviewing:  ['approved', 'collecting', 'blocked'],
  approved:   ['released', 'reviewing', 'blocked'],
  released:   ['blocked'],
  blocked:    ['draft', 'collecting', 'reviewing', 'approved'],
} as const

